import type { ClientMeta } from "../types";
import {
  SCALE_RADII,
  TOLERANCE_THRESHOLDS,
  useViewerStore,
  type ScaleRadius,
  type ToleranceThreshold,
} from "../store";
import { lavaCssGradient } from "../colormap";
import { Section } from "./Section";
import { Toggle } from "./Toggle";
import { Slider } from "./Slider";
import { RangeSlider } from "./RangeSlider";

interface LeftPanelProps {
  meta: ClientMeta;
}

export function LeftPanel({ meta }: LeftPanelProps) {
  const lowECount = meta.low_emissivity_regions?.face_id_count ?? 0;

  return (
    <aside
      className="w-72 shrink-0 overflow-y-auto"
      style={{
        background: "white",
        borderRight: "1px solid var(--color-mist)",
      }}
    >
      <div className="p-6 space-y-8">
        <Section title="Layers">
          <PinnedLayerRow label="RGB" />
          <ThermalLayerRow />
          <DisabledLayerRow label="Operator pins" />
        </Section>

        <Section title="View Mode">
          <ViewModeControls />
        </Section>

        <Section title="Pass">
          <PassStub passes={meta.passes.map((p) => p.label)} />
        </Section>

        {lowECount > 0 && (
          <Section title="Notes">
            <p
              style={{
                fontSize: "11px",
                lineHeight: 1.55,
                color: "var(--color-slate)",
              }}
            >
              <strong style={{ color: "var(--color-copper)" }}>
                {lowECount.toLocaleString()}
              </strong>{" "}
              surfaces flagged as reflective metal. Their apparent
              temperature is shown as captured — these readings need
              engineering judgment for absolute accuracy.
            </p>
          </Section>
        )}

        {/* Accuracy evidence — sits separately from the Layers
         *  section because Tolerance isn't a way to view the model,
         *  it's an artefact showing how trustworthy the model is.
         *  Renders nothing if face_truth_residual.f32 isn't shipped
         *  on this bundle. */}
        <AccuracyEvidence />
      </div>
    </aside>
  );
}

function AccuracyEvidence() {
  const available = useViewerStore((s) => s.toleranceAvailable);
  if (!available) return null;
  return (
    <Section title="Accuracy">
      <ToleranceLayerRow />
    </Section>
  );
}

function PinnedLayerRow({ label }: { label: string }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2.5 rounded-md"
      style={{ background: "var(--color-cloud)" }}
    >
      <span style={{ fontSize: "13px", color: "var(--color-ink)" }}>
        {label}
      </span>
      <span
        style={{
          fontSize: "9px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "var(--color-copper)",
        }}
      >
        Always on
      </span>
    </div>
  );
}

function DisabledLayerRow({ label }: { label: string }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2.5 rounded-md"
      style={{ background: "var(--color-cloud)" }}
    >
      <span style={{ fontSize: "13px", color: "var(--color-ink)" }}>
        {label}
      </span>
      <div
        className="rounded-full relative"
        style={{
          width: "32px",
          height: "18px",
          background: "var(--color-mist)",
          opacity: 0.55,
        }}
        aria-disabled
      >
        <div
          className="absolute rounded-full"
          style={{
            width: "14px",
            height: "14px",
            top: "2px",
            left: "2px",
            background: "white",
            boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
          }}
        />
      </div>
    </div>
  );
}

function ThermalLayerRow() {
  const thermalOn = useViewerStore((s) => s.thermalOn);
  const thermalOpacity = useViewerStore((s) => s.thermalOpacity);
  const setThermalOn = useViewerStore((s) => s.setThermalOn);
  const setThermalOpacity = useViewerStore((s) => s.setThermalOpacity);
  const summary = useViewerStore((s) => s.thermalSummary);

  return (
    <div
      className="rounded-md"
      style={{ background: "var(--color-cloud)" }}
    >
      <div className="flex items-center justify-between px-3 py-2.5">
        <span style={{ fontSize: "13px", color: "var(--color-ink)" }}>
          Thermal
        </span>
        <Toggle on={thermalOn} onChange={setThermalOn} label="Thermal layer" />
      </div>
      {thermalOn && (
        <div
          className="px-3 pb-3 pt-1 space-y-3"
          style={{ borderTop: "1px solid var(--color-mist)" }}
        >
          <div>
            <div
              className="flex justify-between mb-1.5"
              style={{
                fontSize: "10px",
                color: "var(--color-stone)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
              }}
            >
              <span>Opacity</span>
              <span style={{ color: "var(--color-slate)" }}>
                {Math.round(thermalOpacity * 100)}%
              </span>
            </div>
            <Slider
              value={thermalOpacity}
              min={0}
              max={1}
              step={0.01}
              onChange={setThermalOpacity}
              label="Thermal opacity"
            />
          </div>
          <ThermalLegend summary={summary} />
        </div>
      )}
    </div>
  );
}

function ToleranceLayerRow() {
  const available = useViewerStore((s) => s.toleranceAvailable);
  const on = useViewerStore((s) => s.toleranceOn);
  const setOn = useViewerStore((s) => s.setToleranceOn);
  const opacity = useViewerStore((s) => s.toleranceOpacity);
  const setOpacity = useViewerStore((s) => s.setToleranceOpacity);
  const threshold = useViewerStore((s) => s.toleranceThreshold);
  const setThreshold = useViewerStore((s) => s.setToleranceThreshold);
  const summary = useViewerStore((s) => s.toleranceSummary);

  if (!available) {
    // No truth_residual data on this bundle — hide the layer row
    // entirely. Don't show a greyed-out toggle; absent data shouldn't
    // advertise itself.
    return null;
  }

  return (
    <div className="rounded-md" style={{ background: "var(--color-cloud)" }}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <span style={{ fontSize: "13px", color: "var(--color-ink)" }}>
          Tolerance
        </span>
        <Toggle on={on} onChange={setOn} label="Tolerance layer" />
      </div>
      {on && (
        <div
          className="px-3 pb-3 pt-1 space-y-3"
          style={{ borderTop: "1px solid var(--color-mist)" }}
        >
          <div>
            <div
              className="flex justify-between mb-1.5"
              style={{
                fontSize: "10px",
                color: "var(--color-stone)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
              }}
            >
              <span>Opacity</span>
              <span style={{ color: "var(--color-slate)" }}>
                {Math.round(opacity * 100)}%
              </span>
            </div>
            <Slider
              value={opacity}
              min={0}
              max={1}
              step={0.01}
              onChange={setOpacity}
              label="Tolerance opacity"
            />
          </div>

          <div>
            <div
              className="mb-1.5"
              style={{
                fontSize: "10px",
                color: "var(--color-stone)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 600,
              }}
            >
              Within
            </div>
            <div className="flex gap-1">
              {TOLERANCE_THRESHOLDS.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setThreshold(t as ToleranceThreshold)}
                  className="flex-1 rounded transition-colors"
                  style={{
                    fontSize: "11px",
                    padding: "5px 0",
                    background:
                      t === threshold
                        ? "var(--color-copper)"
                        : "white",
                    color:
                      t === threshold ? "white" : "var(--color-slate)",
                    fontWeight: t === threshold ? 600 : 500,
                    border:
                      t === threshold
                        ? "1px solid var(--color-copper)"
                        : "1px solid var(--color-mist)",
                    cursor: "pointer",
                  }}
                >
                  {t.toFixed(1)}°C
                </button>
              ))}
            </div>
          </div>

          <ToleranceLegend summary={summary} />
        </div>
      )}
    </div>
  );
}

function ToleranceLegend({
  summary,
}: {
  summary: ReturnType<typeof useViewerStore.getState>["toleranceSummary"];
}) {
  if (!summary) {
    return (
      <p style={{ fontSize: "11px", color: "var(--color-stone)" }}>
        Loading tolerance data…
      </p>
    );
  }
  const pct = Number.isFinite(summary.fraction)
    ? Math.round(summary.fraction * 100)
    : null;
  return (
    <div>
      <div className="flex gap-2 items-center" style={{ fontSize: "10px" }}>
        <SwatchLabel color="var(--color-sage)" label="Within" />
        <SwatchLabel color="var(--color-copper)" label="Outside" />
      </div>
      <p
        className="mt-2"
        style={{
          fontSize: "11px",
          lineHeight: 1.5,
          color: "var(--color-slate)",
        }}
      >
        {pct !== null ? (
          <>
            <strong style={{ color: "var(--color-sage)" }}>{pct}%</strong>
            {" "}of measured surfaces are within{" "}
            {summary.thresholdC.toFixed(1)}°C of independently-verified
            ground truth.
          </>
        ) : (
          <>No measured surfaces have ground-truth coverage on this pass.</>
        )}
      </p>
    </div>
  );
}

function SwatchLabel({ color, label }: { color: string; label: string }) {
  return (
    <span
      className="flex items-center gap-1.5"
      style={{ color: "var(--color-stone)" }}
    >
      <span
        className="rounded-sm"
        style={{
          width: "10px",
          height: "10px",
          background: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function ThermalLegend({
  summary,
}: {
  summary: ReturnType<typeof useViewerStore.getState>["thermalSummary"];
}) {
  if (!summary) {
    return (
      <p style={{ fontSize: "11px", color: "var(--color-stone)" }}>
        Loading temperature data…
      </p>
    );
  }
  const isRelative = summary.mode === "relative";
  const fmt = (n: number) => {
    if (isRelative) {
      const sign = n > 0 ? "+" : n < 0 ? "" : " ";
      return `${sign}${n.toFixed(2)}`;
    }
    return `${n.toFixed(1)} °C`;
  };
  return (
    <div>
      <div
        className="rounded-sm"
        style={{
          height: "10px",
          background: lavaCssGradient(),
          border: "1px solid var(--color-mist)",
        }}
      />
      <div
        className="flex justify-between mt-1.5"
        style={{
          fontSize: "10px",
          color: "var(--color-slate)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span>{fmt(summary.rangeMin)}</span>
        {isRelative && <span>0</span>}
        <span>{fmt(summary.rangeMax)}</span>
      </div>
      <p
        className="mt-1.5"
        style={{
          fontSize: "10px",
          color: "var(--color-stone)",
          lineHeight: 1.4,
        }}
      >
        {summary.validCount.toLocaleString()} of{" "}
        {summary.faceCount.toLocaleString()} surfaces measured
        {isRelative && summary.radiusM ? ` · ${summary.radiusM} m` : ""}.
      </p>
    </div>
  );
}

function ViewModeControls() {
  const viewMode = useViewerStore((s) => s.viewMode);
  const scaleRadius = useViewerStore((s) => s.scaleRadius);
  const setViewMode = useViewerStore((s) => s.setViewMode);
  const setScaleRadius = useViewerStore((s) => s.setScaleRadius);
  const thermalOn = useViewerStore((s) => s.thermalOn);
  const summary = useViewerStore((s) => s.thermalSummary);
  const bandLo = useViewerStore((s) => s.bandLo);
  const bandHi = useViewerStore((s) => s.bandHi);
  const setBand = useViewerStore((s) => s.setBand);

  const isRelative = viewMode === "relative";
  const fmt = (n: number) => {
    if (isRelative) {
      const sign = n > 0 ? "+" : n < 0 ? "" : " ";
      return `${sign}${n.toFixed(2)}`;
    }
    return `${n.toFixed(1)} °C`;
  };

  // Slider step relative to the data range — gives ~100 stops.
  const step = summary
    ? Math.max((summary.rangeMax - summary.rangeMin) / 100, 0.01)
    : 0.1;

  return (
    <div style={{ opacity: thermalOn ? 1 : 0.55 }}>
      {/* Mode tabs */}
      <div className="flex gap-1.5">
        <ModeTab
          label="Absolute"
          active={viewMode === "absolute"}
          onClick={() => setViewMode("absolute")}
        />
        <ModeTab
          label="Relative"
          active={isRelative}
          onClick={() => setViewMode("relative")}
        />
      </div>

      <p
        className="mt-3"
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: "12.5px",
          lineHeight: 1.45,
          color: "var(--color-slate)",
        }}
      >
        {isRelative
          ? "Show surfaces that are hotter or colder than their neighbours by some amount."
          : "Shows the actual temperature reading on every surface."}
      </p>

      {/* Range control. Absolute = dual-handle Min/Max. Relative =
       *  direction toggle (Cooler/Warmer) and only after a direction
       *  is picked: the comparison-scale slider, then the dual-handle
       *  range scoped to that side. Scale is meaningless until there's
       *  a question to ask, so it stays hidden until the user picks. */}
      {summary && (
        <div className="mt-5">
          {isRelative ? (
            <RelativeRangeControl
              summary={summary}
              bandLo={bandLo}
              bandHi={bandHi}
              setBand={setBand}
              step={step}
              fmt={fmt}
              scaleRadius={scaleRadius}
              setScaleRadius={setScaleRadius}
            />
          ) : (
            <AbsoluteRangeControl
              summary={summary}
              bandLo={bandLo}
              bandHi={bandHi}
              setBand={setBand}
              step={step}
              fmt={fmt}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface RangeControlProps {
  summary: NonNullable<ReturnType<typeof useViewerStore.getState>["thermalSummary"]>;
  bandLo: number;
  bandHi: number;
  setBand: (lo: number, hi: number) => void;
  step: number;
  fmt: (n: number) => string;
}

interface RelativeRangeControlProps extends RangeControlProps {
  scaleRadius: ScaleRadius;
  setScaleRadius: (r: ScaleRadius) => void;
}

function AbsoluteRangeControl({
  summary,
  bandLo,
  bandHi,
  setBand,
  step,
  fmt,
}: RangeControlProps) {
  return (
    <>
      <div
        className="flex justify-between mb-2"
        style={{
          fontSize: "10px",
          color: "var(--color-stone)",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontWeight: 600,
        }}
      >
        <span>Temperature</span>
        <span
          style={{
            color: "var(--color-slate)",
            fontVariantNumeric: "tabular-nums",
            textTransform: "none",
            letterSpacing: "0",
          }}
        >
          {fmt(bandLo)} – {fmt(bandHi)}
        </span>
      </div>
      <RangeSlider
        min={summary.rangeMin}
        max={summary.rangeMax}
        step={step}
        lo={Math.max(bandLo, summary.rangeMin)}
        hi={Math.min(bandHi, summary.rangeMax)}
        onChange={setBand}
        format={fmt}
      />
      <p
        className="mt-2"
        style={{
          fontSize: "10px",
          lineHeight: 1.5,
          color: "var(--color-stone)",
        }}
      >
        Surfaces within this temperature range show on the background
        model.
      </p>
    </>
  );
}

function RelativeRangeControl({
  summary,
  bandLo,
  bandHi,
  setBand,
  step,
  fmt,
  scaleRadius,
  setScaleRadius,
}: RelativeRangeControlProps) {
  // Direction is derived from the active band's sign. We refuse to
  // hold a band that straddles zero — it cancels visually and
  // defeats the question "warmer or cooler?" So band is always
  // either [0, +x] (warmer), [-x, 0] (cooler), or [0, 0] (idle).
  const direction: "warmer" | "cooler" | null =
    bandHi > 0 ? "warmer" : bandLo < 0 ? "cooler" : null;

  const setDirection = (next: "warmer" | "cooler") => {
    // Force a definitively-signed band so direction-derivation reads
    // unambiguously even if summary momentarily has stale values
    // from the previous mode (race during mode-toggle when the
    // Relative delta is still in flight). Math.max/min guards
    // against rangeMax==0 / rangeMin==0 edge cases.
    if (next === "warmer") {
      const top = Math.max(summary.rangeMax, 0.01);
      setBand(0, top);
    } else {
      const bottom = Math.min(summary.rangeMin, -0.01);
      setBand(bottom, 0);
    }
  };

  // Magnitude readout — clients read "Cooler by 0.30 to 1.00°" more
  // naturally than the signed form "−1.00 to −0.30".
  const magLo = direction === "warmer" ? bandLo : -bandHi;
  const magHi = direction === "warmer" ? bandHi : -bandLo;

  const fmtMag = (n: number) => `${n.toFixed(2)}°`;

  return (
    <>
      <div className="flex gap-1.5">
        <DirectionButton
          label="Cooler"
          active={direction === "cooler"}
          onClick={() => setDirection("cooler")}
        />
        <DirectionButton
          label="Warmer"
          active={direction === "warmer"}
          onClick={() => setDirection("warmer")}
        />
      </div>

      {direction === null ? (
        <p
          className="mt-3"
          style={{
            fontSize: "11px",
            lineHeight: 1.5,
            color: "var(--color-stone)",
          }}
        >
          Pick a direction. Cooler highlights surfaces below their
          neighbours; Warmer highlights surfaces above.
        </p>
      ) : (
        <>
          {/* Scale slider — only appears once a direction is set.
           *  Meaningless to ask "compared at what scale" before the
           *  user has picked what to compare. */}
          <div className="mt-4">
            <ScaleSlider value={scaleRadius} onChange={setScaleRadius} />
          </div>

          <div className="mt-5">
            <div
              className="flex justify-between mb-2"
              style={{
                fontSize: "10px",
                color: "var(--color-stone)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontWeight: 600,
              }}
            >
              <span>{direction === "warmer" ? "Warmer by" : "Cooler by"}</span>
              <span
                style={{
                  color: "var(--color-slate)",
                  fontVariantNumeric: "tabular-nums",
                  textTransform: "none",
                  letterSpacing: "0",
                }}
              >
                {fmtMag(magLo)} – {fmtMag(magHi)}
              </span>
            </div>
            {direction === "warmer" ? (
              <RangeSlider
                min={0}
                max={summary.rangeMax}
                step={step}
                lo={Math.max(bandLo, 0)}
                hi={Math.max(bandHi, step)}
                onChange={(lo, hi) => setBand(lo, hi)}
                format={fmt}
              />
            ) : (
              <RangeSlider
                min={summary.rangeMin}
                max={0}
                step={step}
                lo={Math.min(bandLo, -step)}
                hi={Math.min(bandHi, 0)}
                onChange={(lo, hi) => setBand(lo, hi)}
                format={fmt}
              />
            )}
            <p
              className="mt-2"
              style={{
                fontSize: "10px",
                lineHeight: 1.5,
                color: "var(--color-stone)",
              }}
            >
              Surfaces {direction === "warmer" ? "warmer" : "cooler"} than
              their neighbours by {fmtMag(magLo)} to {fmtMag(magHi)} show on
              the background model.
            </p>
          </div>
        </>
      )}
    </>
  );
}

interface DirectionButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function DirectionButton({ label, active, onClick }: DirectionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "7px 0",
        fontSize: "10.5px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        background: active ? "var(--color-copper)" : "transparent",
        color: active ? "white" : "var(--color-stone)",
        border: active
          ? "1px solid var(--color-copper)"
          : "1px solid var(--color-mist)",
        borderRadius: "4px",
        cursor: "pointer",
        transition: "all 0.12s",
      }}
    >
      {label}
    </button>
  );
}

interface ModeTabProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function ModeTab({ label, active, onClick }: ModeTabProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "7px 0",
        fontSize: "10.5px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
        background: active ? "var(--color-copper)" : "transparent",
        color: active ? "white" : "var(--color-stone)",
        border: active
          ? "1px solid var(--color-copper)"
          : "1px solid var(--color-mist)",
        borderRadius: "4px",
        cursor: "pointer",
        transition: "all 0.12s",
      }}
    >
      {label}
    </button>
  );
}

interface ScaleSliderProps {
  value: ScaleRadius;
  onChange: (next: ScaleRadius) => void;
}

function ScaleSlider({ value, onChange }: ScaleSliderProps) {
  // Snap to one of the precomputed radii. The slider rides on a
  // 0..(N-1) integer index over SCALE_RADII so dragging feels
  // continuous but always lands on a value the bundle has data for.
  const idx = SCALE_RADII.indexOf(value);

  return (
    <div>
      <div
        className="flex justify-between mb-2"
        style={{
          fontSize: "10px",
          color: "var(--color-stone)",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          fontWeight: 600,
        }}
      >
        <span>Compared to</span>
        <span
          style={{
            color: "var(--color-copper)",
            fontVariantNumeric: "tabular-nums",
            textTransform: "none",
            letterSpacing: "0",
          }}
        >
          {value} m around it
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={SCALE_RADII.length - 1}
        step={1}
        value={idx >= 0 ? idx : 0}
        onChange={(e) => {
          const i = Number(e.target.value);
          onChange(SCALE_RADII[i]);
        }}
        className="pfd-slider w-full"
        aria-label="Comparison area"
      />
      <div
        className="flex justify-between mt-1.5"
        style={{
          fontSize: "9.5px",
          color: "var(--color-stone)",
          textTransform: "uppercase",
          letterSpacing: "0.10em",
        }}
      >
        <span>Tight · Spot</span>
        <span>Broad · Area</span>
      </div>
    </div>
  );
}

function PassStub({ passes }: { passes: string[] }) {
  if (passes.length <= 1) {
    return (
      <p style={{ fontSize: "12px", color: "var(--color-slate)" }}>
        Single pass — <strong>{passes[0] ?? "—"}</strong>
      </p>
    );
  }
  return (
    <div
      className="px-3 py-2 rounded-md"
      style={{
        background: "var(--color-cloud)",
        opacity: 0.55,
        fontSize: "12px",
        color: "var(--color-slate)",
      }}
      aria-disabled
    >
      {passes[0]} · {passes.length - 1} more
    </div>
  );
}
